import { Hero } from '@/components/sections/Hero';
import { CtaBlock } from '@/components/sections/CtaBlock';
import { PullQuote } from '@/components/ui/PullQuote';
import { Collaboration } from '@/illustrations/Collaboration';
import { SectionHeading } from '@/components/ui/SectionHeading';
import { studioFacts, howWeWork, idealClients } from '@/data/studio';
import { useSeo } from '@/lib/seo';

const values = [
  {
    title: 'Craft first',
    body: 'Every pixel and every line of code comes from a decision we can defend. We would rather ship a little slower than ship something we would need to apologise for.',
  },
  {
    title: 'Plain speech',
    body: 'No jargon in our quotes, no buzzwords in our pitches. We say what we mean and ask you to do the same. Projects run faster that way.',
  },
  {
    title: 'Your ownership',
    body: 'The work is yours on final payment - source files, domains, accounts. If you ever want to leave us for someone else, you can do it in an afternoon.',
  },
  {
    title: 'Small on purpose',
    body: 'We stay small because the good parts happen when the person talking to you is the one doing the work. No account managers, no hand-offs.',
  },
];

export default function About() {
  useSeo({
    title: 'About JUST SEE LTD',
    description: 'A small Cyprus studio making logos, websites, and identities with equal attention paid to every detail.',
  });

  return (
    <>
      <Hero
        eyebrow="About us"
        headline="A small studio for work that is supposed to last."
        description="Founded in Cyprus in 2026 by a designer and a developer who were tired of agency bloat and template websites. That is the whole story."
        illustration={<Collaboration className="w-full max-w-lg" />}
      />

      <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32 bg-surface">
        <div className="mx-auto max-w-[1440px] grid lg:grid-cols-[0.9fr_1.1fr] gap-14 lg:gap-24">
          <SectionHeading eyebrow="Mission" title={<>What we are here for.</>} />
          <div className="flex flex-col gap-8 max-w-2xl">
            <PullQuote>
              Make the internet one website at a time less forgettable.
            </PullQuote>
            <div className="prose-custom text-ink-secondary">
              <p>
                Most marketing sites are built in a hurry, from a theme, by someone who will not be around in a year. They all look the same because they come from the same shop.
              </p>
              <p>
                We think small businesses deserve better than that. A logo that means something. A site that reads like the people behind it wrote it. Code a junior developer can maintain. That is the work we want to do for the next ten years.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32">
        <div className="mx-auto max-w-[1440px] flex flex-col gap-14">
          <SectionHeading eyebrow="Values" title="Four rules we will not break." />
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-14">
            {values.map((v) => (
              <article key={v.title} className="flex flex-col gap-3">
                <h3 className="font-display text-3xl font-medium">{v.title}</h3>
                <p className="text-ink-secondary leading-relaxed max-w-xl">{v.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32 bg-surface border-t border-border">
        <div className="mx-auto max-w-[1440px] grid lg:grid-cols-[0.9fr_1.1fr] gap-14 lg:gap-24">
          <SectionHeading eyebrow="Company" title="On paper." />
          <dl className="grid sm:grid-cols-2 gap-x-10 gap-y-6">
            <div className="flex flex-col gap-1">
              <dt className="mono-meta text-ink-muted">Legal name</dt>
              <dd className="font-sans text-lg">{studioFacts.legalName}</dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="mono-meta text-ink-muted">Registration</dt>
              <dd className="font-sans text-lg">{studioFacts.registrationCountry} · {studioFacts.registrationNumber}</dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="mono-meta text-ink-muted">VAT</dt>
              <dd className="font-sans text-lg">{studioFacts.vatNumber}</dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="mono-meta text-ink-muted">Founded</dt>
              <dd className="font-sans text-lg">{studioFacts.founded}</dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="mono-meta text-ink-muted">Timezone</dt>
              <dd className="font-sans text-lg">{studioFacts.timezone}</dd>
            </div>
            <div className="flex flex-col gap-1">
              <dt className="mono-meta text-ink-muted">Hours</dt>
              <dd className="font-sans text-lg">{studioFacts.workingHours}</dd>
            </div>
            <div className="flex flex-col gap-1 sm:col-span-2">
              <dt className="mono-meta text-ink-muted">Address</dt>
              <dd className="font-sans text-lg whitespace-pre-line">{studioFacts.addressLines.join('\n')}</dd>
            </div>
          </dl>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32">
        <div className="mx-auto max-w-[1440px] grid lg:grid-cols-[0.9fr_1.1fr] gap-14 lg:gap-24">
          <SectionHeading eyebrow="How we work" title="No mystery, no managers." />
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-12">
            {howWeWork.map((item) => (
              <article key={item.title} className="flex flex-col gap-3">
                <h3 className="font-display text-2xl font-medium">{item.title}</h3>
                <p className="text-ink-secondary leading-relaxed">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32 bg-surface">
        <div className="mx-auto max-w-[1440px] flex flex-col gap-14">
          <SectionHeading eyebrow="Who we are for" title={<>And who we are not.</>} />
          <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
            <div className="border-t border-accent pt-6">
              <h3 className="mono-meta text-accent mb-5">For</h3>
              <ul className="flex flex-col gap-4">
                {idealClients.forWho.map((item) => (
                  <li key={item} className="text-ink-primary leading-relaxed">{item}</li>
                ))}
              </ul>
            </div>
            <div className="border-t border-border pt-6">
              <h3 className="mono-meta text-ink-muted mb-5">Not for</h3>
              <ul className="flex flex-col gap-4">
                {idealClients.notForWho.map((item) => (
                  <li key={item} className="text-ink-muted leading-relaxed">{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <CtaBlock />
    </>
  );
}
